import { computed, ref, shallowRef, watch } from 'vue';
import { defineStore } from 'pinia';
import dayjs from 'dayjs';
import { currentGtfsDate } from '@pysae/transit-core/service-day';
import Common from '@/libs/common';
import { buildNetwork, cachedNetwork, type Network } from '@/libs/journey/network';
import { searchVerifiedJourneys } from '@/libs/journey/verified-journeys';
import {
  measureWalks,
  trackJourney,
  walksToVerify,
  type Journey,
  type JourneyMode,
  type JourneyResults,
  type LatLon,
  type Endpoint,
  type JourneyQuery,
  type TrackedJourney,
} from '@/libs/journey/journeys';
import { fetchWalks, WALK_BUDGET_MS } from '@/libs/walking';
import { validityRange, type Place } from '@/libs/journey/places';
import type { RealtimeSnapshot } from '@/libs/journey/realtime';
import type { Trip } from '@/@types/gtfs-ressource';
import { useAppStore } from './app';
import { useDevicesStore } from './devices';
import { useGroupStore } from './group';
import { useGtfsStore } from './gtfs';
import { useTripUpdatesStore } from './trip-updates';

/** « Partir maintenant », « Partir à » or « Arriver à ». */
export type TimeMode = 'now' | JourneyMode;

/** Why there is no journey to list, or `ready` when there is. */
export type JourneyStatus =
  | 'noDestination'
  | 'gtfsLoading'
  | 'positionUnavailable'
  | 'sameStop'
  | 'outOfValidity'
  | 'loading'
  | 'noStopNearby'
  | 'noStopNearDestination'
  | 'noJourney'
  | 'ready';

const DAY = 86400;
const ORIGIN_MOVE_TO_SEARCH_M = 100;

const nowSeconds = () => Math.floor(Date.now() / 1000);

const distanceMeters = (a: LatLon, b: LatLon) =>
  Common.coordDistance({ latitude: a.lat, longitude: a.lon }, { latitude: b.lat, longitude: b.lon });

export const useJourneyStore = defineStore('journey', () => {
  const appStore = useAppStore();
  const devicesStore = useDevicesStore();
  const groupStore = useGroupStore();
  const gtfsStore = useGtfsStore();
  const tripUpdatesStore = useTripUpdatesStore();

  const origin = ref<Place>({ kind: 'position' });
  const destination = ref<Place>();
  const timeMode = ref<TimeMode>('now');
  /** The time picked for « Partir à » / « Arriver à », in epoch seconds. */
  const chosenTime = ref<number>(nowSeconds());

  const results = shallowRef<JourneyResults>({ journeys: [] });
  const isComputing = ref<boolean>(false);
  /** The page is open: searches follow the inputs and the live data only then. */
  const isActive = ref<boolean>(false);

  let resultsNetwork: Network | undefined;
  let resultsQueryTime = 0;
  let resultsMode: JourneyMode = 'depart';

  const tracked = shallowRef<TrackedJourney>();
  let trackedBase: Journey | undefined;
  let trackedNetwork: Network | undefined;

  const isGtfsReady = computed<boolean>(() => {
    const loading = appStore.isLoading;
    const gtfsLoading = typeof loading === 'boolean' ? false : Boolean(loading.gtfs);
    return !gtfsLoading && Object.keys(gtfsStore.gtfsData.trips).length > 0;
  });

  const originPosition = computed<LatLon | undefined>(() => {
    if (origin.value.kind === 'point') return { lat: origin.value.lat, lon: origin.value.lon };
    if (origin.value.kind === 'position') return appStore.positionGeolocation;
    return undefined;
  });

  // The followed position moves every few seconds: search again only once the passenger has really moved.
  const searchedOriginPosition = ref<LatLon>();
  watch(
    originPosition,
    position => {
      const previous = searchedOriginPosition.value;
      if (position && previous && distanceMeters(position, previous) < ORIGIN_MOVE_TO_SEARCH_M) return;
      searchedOriginPosition.value = position;
    },
    { immediate: true },
  );

  const validity = computed(() =>
    validityRange(Object.values(gtfsStore.gtfsData.calendars), gtfsStore.gtfsData.calendarDates),
  );

  const mode = computed<JourneyMode>(() => (timeMode.value === 'arrive' ? 'arrive' : 'depart'));

  function queryTime(): number {
    return timeMode.value === 'now' ? nowSeconds() : chosenTime.value;
  }

  /** The service day of `time` on the network's clock, like the realtime snapshot's `today`. */
  function serviceDayOf(time: number): string {
    return currentGtfsDate(groupStore.group?.tz || 'Europe/Paris', new Date(time * 1000));
  }

  /** Every quay a stop stands for once merged with its namesakes. */
  function quaysOf(stopId: string): string[] {
    const canonical = gtfsStore.getCanonicalStopId(stopId);
    return gtfsStore.stations[canonical] ?? [canonical];
  }

  const blocker = computed<JourneyStatus | undefined>(() => {
    if (!destination.value || destination.value.kind === 'position') return 'noDestination';
    if (!isGtfsReady.value) return 'gtfsLoading';
    if (origin.value.kind === 'position' && !appStore.positionGeolocation) return 'positionUnavailable';
    if (
      origin.value.kind === 'stop' &&
      destination.value.kind === 'stop' &&
      gtfsStore.getCanonicalStopId(origin.value.stopId) ===
        gtfsStore.getCanonicalStopId(destination.value.stopId)
    ) {
      return 'sameStop';
    }
    const day = serviceDayOf(queryTime());
    if (validity.value && (day < validity.value.start || day > validity.value.end)) return 'outOfValidity';
    return undefined;
  });

  const status = computed<JourneyStatus>(() => {
    if (blocker.value) return blocker.value;
    if (isComputing.value) return 'loading';
    if (results.value.originUnreachable) return 'noStopNearby';
    if (results.value.destinationUnreachable) return 'noStopNearDestination';
    return results.value.journeys.length > 0 ? 'ready' : 'noJourney';
  });

  /** Trips of the day, plus those of the day before still running past midnight. */
  function networkOfDay(dateGtfs: string): Network {
    const previousDay = dayjs(dateGtfs, 'YYYYMMDD').subtract(1, 'day').format('YYYYMMDD');
    const passesMidnight = (trip: Trip) => trip.stop_times.some(st => st.arrival_time >= DAY);
    return cachedNetwork(groupStore.group?.current_file ?? '', dateGtfs, () =>
      buildNetwork({
        stops: gtfsStore.gtfsData.stops,
        canonicalOf: gtfsStore.canonicalOf,
        tripsByServiceDate: [
          {
            serviceDate: previousDay,
            trips: Object.values(gtfsStore.getTripsOfDay(previousDay)).filter(passesMidnight),
          },
          { serviceDate: dateGtfs, trips: Object.values(gtfsStore.getTripsOfDay(dateGtfs)) },
        ],
      }),
    );
  }

  function realtimeSnapshot(): RealtimeSnapshot {
    return {
      devices: devicesStore.onlineDevices,
      tripUpdates: tripUpdatesStore.tripUpdates ?? [],
      today: appStore.todayDateGtfs,
      now: nowSeconds(),
    };
  }

  function searchEndpoint(place: Place | undefined): Endpoint | undefined {
    if (place?.kind === 'stop') {
      const stopIds = quaysOf(place.stopId);
      const quays = stopIds.flatMap(stopId => {
        const stop = gtfsStore.gtfsData.stops[stopId];
        return stop ? [{ stopId, lat: stop.stop_lat, lon: stop.stop_lon }] : [];
      });
      return { kind: 'stops', stopIds, quays };
    }
    if (place?.kind === 'point') return { kind: 'position', lat: place.lat, lon: place.lon };
    if (place?.kind === 'position' && appStore.positionGeolocation) {
      return { kind: 'position', ...appStore.positionGeolocation };
    }
    return undefined;
  }

  let pendingSearch: number | undefined;
  // A search waits for the walks: only the latest one may land.
  let searchCount = 0;

  /**
   * RAPTOR on estimated walks finds the candidates, their walks are measured along
   * the streets within the budget, and only the journeys standing the real walks are
   * shown. A `silent` refresh keeps the journeys listed meanwhile: the passenger is reading them.
   */
  function search({ silent = false }: { silent?: boolean } = {}) {
    window.clearTimeout(pendingSearch);
    const from = searchEndpoint(origin.value);
    const to = searchEndpoint(destination.value);
    const searchId = ++searchCount;
    if (blocker.value || !from || !to) {
      results.value = { journeys: [] };
      isComputing.value = false;
      return;
    }
    if (!silent) isComputing.value = true;
    // Deferred by a tick, so that the loading state gets painted before RAPTOR runs.
    pendingSearch = window.setTimeout(async () => {
      try {
        const time = queryTime();
        const network = networkOfDay(serviceDayOf(time));
        const query: JourneyQuery = {
          origin: from,
          destination: to,
          mode: mode.value,
          time,
          realtime: realtimeSnapshot(),
          shapes: gtfsStore.gtfsData.shapes,
        };
        const journeys = await searchVerifiedJourneys(network, query, {
          measure: fetchWalks,
          budgetMs: WALK_BUDGET_MS,
          isStale: () => searchId !== searchCount,
        });
        if (!journeys) return;
        results.value = journeys;
        resultsNetwork = network;
        resultsQueryTime = time;
        resultsMode = query.mode;
      } finally {
        if (searchId === searchCount) isComputing.value = false;
      }
    });
  }

  function setOrigin(value: Place) {
    origin.value = value;
  }

  function setDestination(value: Place | undefined) {
    destination.value = value;
  }

  function setTime(value: TimeMode, time?: number) {
    timeMode.value = value;
    if (time !== undefined) chosenTime.value = time;
  }

  /** A search still waiting for its walks must not land: under the journey followed, or once the page is left. */
  function cancelSearch() {
    window.clearTimeout(pendingSearch);
    searchCount++;
    isComputing.value = false;
  }

  function startTracking(journeyId: string) {
    const journey = results.value.journeys.find(candidate => candidate.id === journeyId);
    if (!journey || !resultsNetwork) return;
    cancelSearch();
    trackedBase = journey;
    trackedNetwork = resultsNetwork;
    tracked.value = { journey };
    refreshTracking();
  }

  /** New estimated times for the journey followed, without searching again. */
  function refreshTracking() {
    if (!trackedBase || !trackedNetwork) return;
    tracked.value = trackJourney(
      trackedNetwork,
      trackedBase,
      realtimeSnapshot(),
      resultsMode,
      resultsQueryTime,
    );
  }

  /** Walks of the journey followed still on their estimate, measured again (the connection may be back). */
  async function retryTrackedWalks() {
    const base = trackedBase;
    const requests = base ? walksToVerify([base]) : [];
    if (!base || requests.length === 0) return;
    const quotes = await fetchWalks(requests);
    if (trackedBase !== base || quotes.size === 0) return;
    trackedBase = measureWalks(base, quotes, resultsMode, resultsQueryTime);
    refreshTracking();
  }

  /** The list was left as it was when the tracking started: it lists buses gone since. */
  function stopTracking() {
    const wasTracking = Boolean(tracked.value);
    tracked.value = undefined;
    trackedBase = undefined;
    trackedNetwork = undefined;
    if (wasTracking && isActive.value) search({ silent: true });
  }

  /** « Recalculer »: a connection is lost, search again from now. */
  function recalculate() {
    stopTracking();
    timeMode.value = 'now';
    search();
  }

  watch(
    [isActive, origin, destination, timeMode, chosenTime, searchedOriginPosition, isGtfsReady],
    () => {
      if (isActive.value && !tracked.value) search();
    },
    { deep: true },
  );

  watch(
    () => tripUpdatesStore.tripUpdates,
    () => {
      if (!isActive.value) return;
      if (tracked.value) {
        refreshTracking();
        retryTrackedWalks();
      } else {
        search({ silent: true });
      }
    },
  );

  watch(
    () => devicesStore.onlineDevices,
    () => {
      if (isActive.value && tracked.value) refreshTracking();
    },
  );

  function $reset() {
    stopTracking();
    cancelSearch();
    origin.value = { kind: 'position' };
    destination.value = undefined;
    timeMode.value = 'now';
    chosenTime.value = nowSeconds();
    results.value = { journeys: [] };
  }

  return {
    origin,
    destination,
    timeMode,
    chosenTime,
    mode,
    results,
    status,
    tracked,
    validity,
    isActive,
    quaysOf,
    search,
    setOrigin,
    setDestination,
    setTime,
    startTracking,
    refreshTracking,
    stopTracking,
    recalculate,
    $reset,
  };
});
